// Mobile-side authentication & vault management.
// All crypto runs locally — server never sees master password.

import { ApiClient } from "@ext/api/client";
import {
  decryptBlob,
  deriveAuthHash,
  deriveMasterKey,
  encryptBlob,
  fromB64,
  toB64,
  wipe,
} from "@ext/crypto";
import type { ItemData, LoginItemData, ServerItem } from "@ext/types";

import { enrollFaceId, faceIdAvailable, unwrapVaultKey } from "./faceid";
import { clearFaceId, clearState, getFaceIdBundle, loadState, saveState } from "./storage";

export interface DecryptedItem {
  id: string;
  revision: number;
  updated_at: string;
  data: LoginItemData;
}

export class Session {
  private vaultKey: Uint8Array | null = null;
  private accessToken: string | null = null;
  private items: DecryptedItem[] = [];
  private api: ApiClient;

  constructor() {
    // Empty serverUrl → fetch with absolute paths against same origin.
    this.api = new ApiClient(
      "",
      () => this.accessToken,
      async () => {
        // Auto-refresh on 401: try the stored refresh token; if accepted, the
        // calling request gets retried once.
        const state = loadState();
        if (!state.refreshToken) return false;
        try {
          const tmp = new ApiClient("");
          const r = await tmp.refresh(state.refreshToken);
          this.accessToken = r.access_token;
          saveState({ refreshToken: r.refresh_token });
          return true;
        } catch {
          return false;
        }
      },
    );
  }

  get unlocked(): boolean {
    return this.vaultKey !== null;
  }

  get cachedItems(): DecryptedItem[] {
    return this.items;
  }

  async unlock(masterPassword: string): Promise<void> {
    const cached = loadState();
    let saltB64 = cached.kdfSaltB64;
    let opslimit = cached.kdfOpslimit;
    let memlimit = cached.kdfMemlimit;

    if (!saltB64 || !opslimit || !memlimit) {
      const status = await this.api.getSetupStatus();
      if (!status.configured) throw new Error("Serwer nie jest skonfigurowany");
      saltB64 = status.kdf_salt_b64!;
      opslimit = status.kdf_opslimit!;
      memlimit = status.kdf_memlimit!;
    }

    let authHash: Uint8Array | null = null;
    let masterKey: Uint8Array | null = null;
    try {
      authHash = await deriveAuthHash(masterPassword, saltB64, opslimit, memlimit);
      const loginResp = await this.api.login(await toB64(authHash));
      this.accessToken = loginResp.access_token;

      masterKey = await deriveMasterKey(
        masterPassword,
        loginResp.kdf_salt_b64,
        loginResp.kdf_opslimit,
        loginResp.kdf_memlimit,
      );
      const vkBytes = await decryptBlob(
        await fromB64(loginResp.encrypted_vault_key.ciphertext_b64),
        await fromB64(loginResp.encrypted_vault_key.nonce_b64),
        masterKey,
      );
      this.vaultKey = vkBytes;

      saveState({
        kdfSaltB64: loginResp.kdf_salt_b64,
        kdfOpslimit: loginResp.kdf_opslimit,
        kdfMemlimit: loginResp.kdf_memlimit,
        encryptedVaultKey: {
          ciphertextB64: loginResp.encrypted_vault_key.ciphertext_b64,
          nonceB64: loginResp.encrypted_vault_key.nonce_b64,
        },
        refreshToken: loginResp.refresh_token,
      });
    } finally {
      await wipe(authHash, masterKey);
    }

    await this.refresh();
  }

  async lock(): Promise<void> {
    await wipe(this.vaultKey);
    this.vaultKey = null;
    this.accessToken = null;
    this.items = [];
  }

  forgetServerState(): void {
    clearState();
  }

  async refresh(): Promise<void> {
    if (!this.vaultKey) throw new Error("locked");
    const serverItems = await this.api.listItems();
    this.items = await Promise.all(serverItems.map((it) => this.decryptItem(it)));
  }

  private async decryptItem(it: ServerItem): Promise<DecryptedItem> {
    if (!this.vaultKey) throw new Error("locked");
    const plain = await decryptBlob(
      await fromB64(it.ciphertext_b64),
      await fromB64(it.nonce_b64),
      this.vaultKey,
    );
    const data = JSON.parse(new TextDecoder().decode(plain)) as ItemData;
    return { id: it.id, revision: it.revision, updated_at: it.updated_at, data: data as LoginItemData };
  }

  async encryptAndUpload(data: LoginItemData): Promise<DecryptedItem> {
    if (!this.vaultKey) throw new Error("locked");
    const id = crypto.randomUUID();
    const { ciphertext, nonce } = await encryptBlob(
      new TextEncoder().encode(JSON.stringify(data)),
      this.vaultKey,
    );
    const created = await this.api.createItem({
      id,
      crypto_version: 1,
      ciphertext_b64: await toB64(ciphertext),
      nonce_b64: await toB64(nonce),
    });
    const dec = await this.decryptItem(created);
    this.items = [...this.items, dec];
    return dec;
  }

  async updateExisting(id: string, data: LoginItemData): Promise<DecryptedItem> {
    if (!this.vaultKey) throw new Error("locked");
    const { ciphertext, nonce } = await encryptBlob(
      new TextEncoder().encode(JSON.stringify(data)),
      this.vaultKey,
    );
    const updated = await this.api.updateItem(id, {
      ciphertext_b64: await toB64(ciphertext),
      nonce_b64: await toB64(nonce),
      crypto_version: 1,
    });
    const dec = await this.decryptItem(updated);
    this.items = this.items.map((it) => (it.id === id ? dec : it));
    return dec;
  }

  async deleteExisting(id: string): Promise<void> {
    if (!this.vaultKey) throw new Error("locked");
    await this.api.deleteItem(id);
    this.items = this.items.filter((it) => it.id !== id);
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    if (!this.vaultKey) throw new Error("locked");
    const state = loadState();
    if (!state.kdfSaltB64 || !state.kdfOpslimit || !state.kdfMemlimit) {
      throw new Error("Brak parametrów KDF — zaloguj się hasłem głównym i spróbuj ponownie");
    }
    const saltB64 = state.kdfSaltB64;
    const opslimit = state.kdfOpslimit;
    const memlimit = state.kdfMemlimit;

    let oldAuth: Uint8Array | null = null;
    let newAuth: Uint8Array | null = null;
    let newMasterKey: Uint8Array | null = null;
    try {
      oldAuth = await deriveAuthHash(oldPassword, saltB64, opslimit, memlimit);
      newAuth = await deriveAuthHash(newPassword, saltB64, opslimit, memlimit);
      newMasterKey = await deriveMasterKey(newPassword, saltB64, opslimit, memlimit);

      const { ciphertext: newVkCt, nonce: newVkNonce } = await encryptBlob(
        this.vaultKey,
        newMasterKey,
      );

      await this.api.changePassword({
        old_auth_hash_b64: await toB64(oldAuth),
        new_auth_hash_b64: await toB64(newAuth),
        new_encrypted_vault_key: {
          ciphertext_b64: await toB64(newVkCt),
          nonce_b64: await toB64(newVkNonce),
        },
        new_kdf_salt_b64: null,
      });

      // Server bumped token_version — log in again to get fresh tokens.
      const loginResp = await this.api.login(await toB64(newAuth));
      this.accessToken = loginResp.access_token;

      saveState({
        refreshToken: loginResp.refresh_token,
        encryptedVaultKey: {
          ciphertextB64: loginResp.encrypted_vault_key.ciphertext_b64,
          nonceB64: loginResp.encrypted_vault_key.nonce_b64,
        },
      });
    } finally {
      await wipe(oldAuth, newAuth, newMasterKey);
    }
  }

  // ---------- Face ID ----------

  async faceIdSupported(): Promise<boolean> {
    return await faceIdAvailable();
  }

  faceIdEnrolled(): boolean {
    return getFaceIdBundle() !== null;
  }

  async enableFaceId(): Promise<void> {
    if (!this.vaultKey) throw new Error("vault musi być odblokowany");
    const bundle = await enrollFaceId(this.vaultKey);
    saveState({
      faceIdCredentialIdB64: bundle.credentialIdB64,
      faceIdSaltB64: bundle.saltB64,
      faceIdNonceB64: bundle.nonceB64,
      faceIdCiphertextB64: bundle.ciphertextB64,
      faceIdAsked: true,
    });
  }

  disableFaceId(): void {
    clearFaceId();
  }

  /** Unlock the vault using Face ID + the stored refresh token. */
  async unlockWithFaceId(): Promise<void> {
    const bundle = getFaceIdBundle();
    if (!bundle) throw new Error("Face ID nie jest skonfigurowane");
    const state = loadState();
    if (!state.refreshToken) {
      // We have no way to get an access_token without re-logging in.
      clearFaceId();
      throw new Error("Sesja wygasła — odblokuj hasłem głównym, żeby ponownie włączyć Face ID");
    }

    // 1. Face ID prompt → PRF → decrypt vault_key
    const vk = await unwrapVaultKey(bundle);
    this.vaultKey = vk;

    // 2. Trade refresh_token for a fresh access_token. The server's refresh
    // endpoint enforces account.token_version, so a stale refresh from before
    // a password change correctly fails.
    try {
      const r = await this.api.refresh(state.refreshToken);
      this.accessToken = r.access_token;
      saveState({ refreshToken: r.refresh_token });
    } catch {
      // Refresh failed (JWT secret rotation, server migration, password change).
      // The vault_key itself is still valid — only the server-side session is dead.
      // Drop the in-memory key and ask for master password, but keep the Face ID
      // enrolment intact so the user can resume biometric unlock after one
      // master-password fallback.
      await wipe(this.vaultKey);
      this.vaultKey = null;
      throw new Error(
        "Sesja wygasła — zaloguj się hasłem głównym jeden raz, potem Face ID znów zadziała.",
      );
    }

    await this.refresh();
  }
}
